/**
 * PageSource — the seam between the FROZEN numbered-pager UX and whatever
 * pagination the backend speaks (approved decision: the pager UI never
 * changes; the adapter does).
 *
 * Adapters:
 *  - numbered adapter: the ZOY-285 Clinic contract — any page fetched
 *    directly by number; total / range / previous / next come from the
 *    server envelope (never counted or guessed client-side).
 *  - array adapter: for backend surfaces that return plain arrays (Auth
 *    members/roles) — client paging with real totals.
 */

export interface PageView<T> {
  rows: readonly T[];
  /** 1-based range of the current page; null while loading/empty. */
  range: { from: number; to: number } | null;
  /** Server total of the same filtered set — null only before the first response. */
  total: number | null;
  page: number; // 1-based
  /** The last page (server totalPages, or the real last for arrays). */
  maxKnownPage: number;
  /** True when maxKnownPage is the actual last page. */
  maxIsExact: boolean;
  hasPrev: boolean;
  hasNext: boolean;
}

/** The envelope members the numbered adapter reads (subset of PagedResult). */
export interface NumberedEnvelope<T> {
  items: readonly T[];
  total: number;
  totalPages: number;
  page: number | null;
  rangeStart: number | null;
  rangeEnd: number | null;
  hasPrevious: boolean | null;
  hasNext: boolean;
}

/**
 * Numbered mode (ZOY-285): the requested page (frontend 0-based index + 1)
 * is echoed by the server; total, totalPages, range and previous/next are
 * taken from the envelope. While the first response is in flight the view
 * is empty with no total (the pager shows nothing it does not know).
 */
export function numberedView<T>(
  requestedPage: number,
  data: NumberedEnvelope<T> | undefined,
): PageView<T> {
  if (data === undefined) {
    return {
      rows: [],
      range: null,
      total: null,
      page: requestedPage,
      maxKnownPage: requestedPage,
      maxIsExact: false,
      hasPrev: false,
      hasNext: false,
    };
  }
  const page = data.page ?? requestedPage;
  const last = Math.max(1, data.totalPages);
  return {
    rows: data.items,
    range:
      data.rangeStart !== null && data.rangeEnd !== null
        ? { from: data.rangeStart, to: data.rangeEnd }
        : null,
    total: data.total,
    page,
    maxKnownPage: Math.max(last, page),
    maxIsExact: true,
    hasPrev: data.hasPrevious ?? page > 1,
    hasNext: data.hasNext,
  };
}

export function arrayView<T>(all: readonly T[], page: number, perPage: number): PageView<T> {
  const total = all.length;
  const last = Math.max(1, Math.ceil(total / perPage));
  const p = Math.min(Math.max(1, page), last);
  const from = (p - 1) * perPage;
  const rows = all.slice(from, from + perPage);
  return {
    rows,
    range: rows.length === 0 ? null : { from: from + 1, to: from + rows.length },
    total,
    page: p,
    maxKnownPage: last,
    maxIsExact: true,
    hasPrev: p > 1,
    hasNext: p < last,
  };
}

export const PER_PAGE_OPTIONS = [10, 20, 50, 100] as const;

/** Windowed page numbers: first, last-known, current ±1 (frozen pager rule). */
export function pageWindow(view: PageView<unknown>): (number | 'gap')[] {
  const pages = new Set<number>([1, view.maxKnownPage, view.page, view.page - 1, view.page + 1]);
  const seq = [...pages].filter((p) => p >= 1 && p <= view.maxKnownPage).sort((a, b) => a - b);
  const out: (number | 'gap')[] = [];
  seq.forEach((p, i) => {
    const prev = seq[i - 1];
    if (i > 0 && prev !== undefined && p - prev > 1) out.push('gap');
    out.push(p);
  });
  return out;
}
